import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { NavigateHeader } from '@/components/atoms/navigate-header'
import { LinkWithArrowIcon } from '@/components/molecules/link-with-arrow-icon'
import { Layout } from '@/components/template/layout'
import { LoadingFull } from '@/components/atoms/loading-full'
import { fetchSpaceReservations } from '@/features/space/space-reservations-list-slice'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { pcScreenStyle } from '@/styles/common'
import { css } from '@emotion/react'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { errorHandler } from '@/libs/errors'

export const SpaceReservationsList: React.FC = () => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const dispatch = useAppDispatch()
  const reservations = useAppSelector(state => state.spaceReservationsList?.reservations ?? [])
  const loading = useAppSelector(state => state.spaceReservationsList?.loading ?? false)
  const design = useAppSelector(state => state.design)

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        await dispatch(fetchSpaceReservations()).unwrap()
      } catch (error) {
        errorHandler({ error })
      }
    }
    fetchReservations()
  }, [dispatch])

  return (
    <Layout>
      <NavigateHeader title={t('Space Reservations')} />
      <LoadingFull loading={loading} />
      <div css={pcScreenStyle}>
        {!loading && isEmpty(reservations) ? (
          <div
            css={[noReservationsBox, { backgroundColor: design?.background.cardColor }]}
          >
            <p css={[noReservationsText, { color: design?.text.textColor }]}>
              {t('You have no space reservations')}
            </p>
          </div>
        ) : !loading ? (
          reservations.map(reservation => (
            <LinkWithArrowIcon
              key={reservation.id}
              linkTo={`/mypage/space-reservations/${reservation.id}`}
            >
              <p css={spaceNameStyle}>{pickByLanguage(reservation.spaceTitle)}</p>
              <span css={dateTimeStyle}>
                {dayjs(reservation.usageFrom).format('YYYY/MM/DD HH:mm')} - {dayjs(reservation.usageTo).format('HH:mm')}
              </span>
              <div
                css={[reservedInfoLine, { borderTopColor: design?.background.backgroundColor }]}
                aria-hidden
              />
              <div css={reservedInfoBlock}>
                <span css={reservedAtStyle} style={{ color: design?.text.textColor }}>
                  {t('Reserved At')}:{' '}
                  {reservation.reservedAt != null
                    ? dayjs(reservation.reservedAt).format('YYYY/MM/DD HH:mm')
                    : '—'}
                </span>
                <span css={[reservedAtStyle, { display: 'block', marginTop: '0.5rem' }]} style={{ color: design?.text.textColor }}>
                  {reservation.hotelName || '—'}
                </span>
              </div>
            </LinkWithArrowIcon>
          ))
        ) : null}
      </div>
    </Layout>
  )
}

const spaceNameStyle = css({
  fontWeight: 'bold',
  marginBottom: '0.5rem',
})

const dateTimeStyle = css({
  fontSize: '12px',
  display: 'block',
  marginBottom: '0.25rem',
})

const reservedAtStyle = css({
  fontSize: '12px',
})

const reservedInfoLine = css({
  borderTop: '1px solid',
  marginTop: '16px',
  marginRight: '2.5rem', // 右端の ＞ アイコンとの間を少し開ける
})

const reservedInfoBlock = css({
  paddingTop: '0.5rem',
})

const noReservationsBox = css({
  padding: '1.5rem 1.25rem',
  marginTop: '1rem',
})

const noReservationsText = css({
  margin: 0,
  textAlign: 'left',
  fontSize: '14px',
  lineHeight: '24px',
  fontWeight: 'bold',
  letterSpacing: '1.4px',
})
